import { getGatedIdacBackendUrl } from '../utilities/env';
import { Action, ThunkAction } from '../types';
import { addBreadcrumb, captureTaggedException } from '../utilities/analytics/error-reporting';

type MfaPromoteChoice = 'edit_mfa' | 'dismiss' | 'edit_passkey';

const loginPathWithErrorMessage = () => {
  const params = new URLSearchParams({
    errorCode: 'invalid.session.token',
    prompt: 'true',
  }).toString();
  return `${window.location.origin}/login?${params}`;
};

type ContinueUrlOptions = {
  additionalSearchParamsEntries?: Array<[string, string]>;
};

export const handleMfaPromoteUserSelection =
  (
    choice: MfaPromoteChoice,
    queryParams?: string | null,
    continueUrlOptions?: ContinueUrlOptions
  ): ThunkAction =>
  dispatch => {
    const path = getGatedIdacBackendUrl(`/v2/mfa/promote${queryParams || ''}`);
    return fetch(path, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ choice }),
    })
      .then(response => {
        if (response.status === 200) {
          return response.json();
        } else {
          throw new Error(`${response.status} ${response.statusText}: /v2/mfa/promote`);
        }
      })
      .then(body => {
        if (!body?.redirectTo) {
          captureTaggedException('/v2/mfa/promote did not return a redirectTo', {
            capabilities: ['capability-name--mfa-promote'],
          });
          dispatch(mfaPromoteRedirect(loginPathWithErrorMessage()));
          return;
        }

        // Immediate redirect URL to /login/authorize, for session distribution
        const redirectUrl = new URL(body.redirectTo);

        // Ultimate destination URL (encoded under 'continue' search param on the redirect URL)
        const continueUrlParam = redirectUrl.searchParams.get('continue');

        if (continueUrlParam && continueUrlOptions?.additionalSearchParamsEntries) {
          // Unwrap continue URL, add search params (e.g. around analytics), and re-wrap it.
          try {
            const continueUrl = new URL(continueUrlParam);

            continueUrlOptions.additionalSearchParamsEntries.forEach(([name, value]) => {
              continueUrl.searchParams.append(name, value);
            });

            redirectUrl.searchParams.set('continue', continueUrl.toString());
          } catch (error) {
            addBreadcrumb({
              category: 'mfa-promote.redirect',
              message: 'Failed to add search params to the continue URL',
              level: 'error',
            });
            captureTaggedException(error, { capabilities: ['capability-name--mfa-promote'] });
          }
        }

        dispatch(mfaPromoteRedirect(redirectUrl.toString()));
      })
      .catch(error => {
        captureTaggedException(error, { capabilities: ['capability-name--mfa-promote'] });
        dispatch(mfaPromoteRedirect(loginPathWithErrorMessage()));
      });
  };

export const mfaPromoteRedirect = (redirectTo: string): Action => ({
  type: 'MFA_PROMOTE_REDIRECT',
  redirectTo,
});
